import { Link } from 'react-router-dom'
import { capitalizedText, formatDate } from '@/lib/format'
import {
  COMPANY_BRANDING,
  DEFAULT_LOGO,
  formatScore,
  hasVacancyStarted,
  scoreTextClass,
  type VacancyRow,
} from './types'

/**
 * Port of `Vacancy.InjectToTemplate` — the card in the vacancy listing.
 *
 * Both listing scripts (vacancy.js and vacancyBeforeLogin.js) built their own
 * copy of this string; the anonymous one simply omitted the bookmark button and
 * the fit-profile score. One component with two flags covers both.
 *
 * The links keep the `/Vacancy/InfoRedirect/{id}` and `/Vacancy/Apply/{id}`
 * shape: those routes take a plain id and bounce to the encoded URL, so the
 * listing never has to know the cipher.
 */
interface VacancyCardProps {
  vacancy: VacancyRow
  /** The bookmark button is signed-in only. */
  showSaveButton: boolean
  isSaved: boolean
  onToggleSave: (vacancy: VacancyRow) => void
  /** The score line renders only when signed in AND the row carries a Score. */
  showScore: boolean
}

export function VacancyCard({
  vacancy,
  showSaveButton,
  isSaved,
  onToggleSave,
  showScore,
}: VacancyCardProps): JSX.Element {
  const started = hasVacancyStarted(vacancy.StartVacancy)
  const branding = vacancy.CompanyName ? COMPANY_BRANDING[vacancy.CompanyName] : undefined
  const logo = branding?.logo ?? DEFAULT_LOGO

  return (
    <div
      className="col-lg-4 col-md-6 wow animate__fadeInUp"
      data-wow-delay="0.2s"
      style={{ visibility: 'visible', animationDelay: '0.2s', animationName: 'fadeInUp' }}
    >
      <div className="card position-relative">
        <div className="card-header p-3 position-relative">
          <img className="img-fluid wid-90" src={logo} alt="Company Logo" />

          {showSaveButton && (
            <button
              type="button"
              className={`btn btn-link btnSaveVacancy position-absolute top-0 end-0 mt-2 me-2 p-1${
                isSaved ? ' saved' : ''
              }`}
              data-vacancy-id={vacancy.trxVacancyID}
              title={isSaved ? 'Remove from saved' : 'Save this vacancy'}
              style={{ border: 'none', background: 'none', fontSize: '1.5rem' }}
              onClick={() => onToggleSave(vacancy)}
            >
              <i
                className={`fas fa-bookmark ${isSaved ? 'text-primary' : 'text-muted'}`}
                style={{ fontSize: '1.5rem' }}
              />
            </button>
          )}
        </div>

        <div className="card-body">
          <h5 className="card-title mb-3">{vacancy.PositionName}</h5>
          <h6 className="card-subtitle mb-3 text-muted">
            {vacancy.CompanyName}
            {branding?.website && (
              <>
                <br />
                <small className="text-muted">
                  <i className=" text-primary" />{' '}
                  <a
                    href={branding.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none"
                  >
                    {branding.website.replace(/^https:\/\//, '')}
                  </a>
                </small>
              </>
            )}
          </h6>

          <ul className="list-inline my-3 card-text text-muted text-sm">
            <li className="list-inline-item">
              <i className="ti ti-user text-primary" />
              <span id="spnCareer" className="m-l-5">
                {vacancy.CareerName}
              </span>
            </li>
            <li className="list-inline-item">
              <i className="ti ti-id text-primary" />
              <span id="spnJobContracts" className="m-l-5">
                {vacancy.JobContractsName}
              </span>
            </li>
            <li className="list-inline-item">
              <i className="ti ti-map-pin text-primary" />
              <span id="spnLocation" className="m-l-5">
                {/* The upstream stores provinces upper-cased; the original
                    lower-cased then title-cased them for display. */}
                {capitalizedText((vacancy.ProvinceName ?? '').toLowerCase())}
              </span>
            </li>
          </ul>

          {!started && (
            <p className="card-text text-muted text-sm">
              <i className="ti ti-calendar-clock text-warning" /> Start Date :{' '}
              {formatDate(vacancy.StartVacancy)}
            </p>
          )}

          <p className="card-text text-muted text-sm">
            <i className="ti ti-calendar-event text-primary" /> Deadline :{' '}
            {formatDate(vacancy.EndVacancy)}
          </p>

          {showScore && vacancy.Score !== null && vacancy.Score !== undefined && (
            <p className="card-text text-muted text-sm fw-bold">
              <i className="ti ti-target text-primary" /> Vacancy fit profile:{' '}
              <span className={scoreTextClass(vacancy.Score)}>{formatScore(vacancy.Score)}</span>
            </p>
          )}
        </div>

        {/*
          `text-center` in the original left the two buttons touching:
          vacancy.js:652-658 concatenated the anchors with no separator at all,
          so "Show More" and "Apply Now" rendered edge to edge. A flex row with
          a gap separates them, and wraps rather than overflowing on a narrow
          card.
        */}
        <div className="card-footer p-2 d-flex flex-wrap justify-content-center gap-2">
          <Link
            to={`/Vacancy/InfoRedirect/${vacancy.trxVacancyID}`}
            className="btn btn-outline-primary"
            id="btnShowMore"
          >
            Show More
          </Link>

          {started ? (
            <Link
              to={`/Vacancy/Apply/${vacancy.trxVacancyID}`}
              className="btn btn-primary btnApplyNow"
              data-vacancy-id={vacancy.trxVacancyID}
            >
              Apply Now
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-secondary"
              disabled
              title={`Vacancy will start on ${formatDate(vacancy.StartVacancy)}`}
            >
              Not Started Yet
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
